import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ModalHeader } from '../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalBody } from '../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalFooter } from '../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { Modal } from '../../../../CommonComponents/OPL/Modal/Modal';
import { Tab, TabList, TabPanel, Tabs } from '../../../../CommonComponents/OPL/Tab';
import { Summary } from './components/Summary/Summary';
import { VestingSchedule } from './components/VestingSchedule/VestingSchedule';
import { FormValues, validation, Grant, Account } from './UpsertGrantModal.meta';
import { GrantToDelete, GrantToUpsert } from '../../StockOptions.meta';
import { useSaveAccountMutation } from '../../api/useSaveAccountMutation';
import { Alert } from '../../../../CommonComponents/OPL/Alert/Alert';
import { useStockOptionsContext } from '../../context/useStockOptionsContext';
import { DeleteStockGrantModal } from '../DeleteStockGrantModal/DeleteStockGrantModal';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import { EXCLUDE_OPTION_VALUE } from './components/Summary/Summary.meta';

interface Props {
  clientId: string;
  grantToUpsert: GrantToUpsert;
  onClose(): void;
}

export const UpsertGrantModal = ({ clientId, grantToUpsert, onClose }: Props) => {
  const [grantToDelete, setGrantToDelete] = useState<GrantToDelete | null>(null);

  const { grant, account, isAddingNewGrant } = grantToUpsert;

  const { options } = useStockOptionsContext();

  const getDefaultCustomAssetBucketIndex = () => {
    if (options.AssetBucketingByManagementStyle) {
      const selectedManagementStyle = options.ManagementStyles.find(
        option => option.id === account.OrionManagementStyleId,
      );

      return selectedManagementStyle?.defaultBucket ?? EXCLUDE_OPTION_VALUE;
    }

    if (account.CustomAssetBucketIndex === null) {
      return options.DefaultAssetBucketIndex;
    }

    return account.CustomAssetBucketIndex;
  };

  const formBag = useForm<FormValues>({
    defaultValues: {
      name: account.AccountName || '',
      owner: account.AccountOwner || '',
      grantType: grant.GrantType,
      marketValue: grant.Value,
      totalShares: grant.TotalShares,
      customAssetBucketIndex: getDefaultCustomAssetBucketIndex(),
      grantYear: grant.GrantYear,
      pricePerShare: grant.PricePerShare,
      expectedReturn: grant.ExpectedReturn,
      assetClass: account.StockOptionAssetClass,
      earmarks: (account.AccountEarmark || []).map(({ Tooltip, Type }) => ({
        label: Tooltip,
        value: Type,
      })),
      vestingList: grant.VestingList && grant.VestingList.length > 0 ? grant.VestingList : [{}],
      orionManagementStyleId: account.OrionManagementStyleId ?? null,
    },
    resolver: yupResolver(validation),
  });

  const {
    mutateAsync: saveAccount,
    isLoading: isSavingAccount,
    error: saveAccountError,
    reset: resetSaveAccount,
  } = useSaveAccountMutation({ clientId });

  const onSubmit = formBag.handleSubmit(async (values: FormValues) => {
    try {
      const grantTypeOption = options.GrantTypeOptions.find(
        ({ value }) => value === values.grantType,
      );

      const newVestingList = (values.vestingList || []).filter(
        vest => typeof vest.VestingYear === 'number' && typeof vest.VestedShares === 'number',
      );

      const newGrant: Grant = {
        GrantType: values.grantType,
        Value: values.marketValue,
        TotalShares: values.totalShares,
        GrantYear: values.grantYear,
        PricePerShare: values.pricePerShare,
        ExpectedReturn: values.expectedReturn,
        GrantTypeName: grantTypeOption?.label || '',
        VestingList: newVestingList,
      };

      let newGrantList: Grant[];

      if (isAddingNewGrant) {
        newGrantList = account.GrantList ? [...account.GrantList, newGrant] : [newGrant];
      } else {
        newGrantList = account.GrantList
          ? account.GrantList.map(g => {
              if (g.Id === grant.Id) {
                return {
                  ...g,
                  ...newGrant,
                };
              }
              return g;
            })
          : [newGrant];
      }

      const newAccount: Account = {
        ...account,
        AccountName: values.name,
        AccountOwner: values.owner,
        StockOptionAssetClass: values.assetClass,
        CustomAssetBucketIndex: values.customAssetBucketIndex,
        AccountEarmark: values.earmarks.map(({ value, label }) => ({
          Type: value,
          Tooltip: label,
        })),
        GrantList: newGrantList,
        OrionManagementStyleId: values.orionManagementStyleId,
      };

      await saveAccount({ clientId, account: newAccount });

      toast.success(`Stock grant ${grant.Id ? 'saved' : 'added'} successfully.`);

      onClose();
    } catch (error) {
      console.error(error);
    }
  });

  return (
    <>
      <Modal
        isOpen
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={false}
        isHidden={!!grantToDelete}
        aria={{ labelledby: 'upsert-grant--modal-header' }}
      >
        <ModalHeader as="h2" id="upsert-grant--modal-header">
          {`${grant.Id ? 'Edit' : 'Add'} Stock Grant`}
        </ModalHeader>
        <ModalCloseButton onClick={onClose} />

        {saveAccountError && (
          <Alert
            variant="error"
            message={saveAccountError.message}
            onRequestClose={resetSaveAccount}
            mb="16"
            mx="24"
          />
        )}

        <FormProvider {...formBag}>
          <form id="upsert_grant__form" onSubmit={onSubmit}>
            <Tabs>
              <TabList px="24">
                <Tab>Summary</Tab>
                <Tab>Vesting Schedule</Tab>
              </TabList>

              <TabPanel>
                <ModalBody style={{ height: '60vh' }}>
                  <Summary isAddingNewGrant={isAddingNewGrant} />
                </ModalBody>
              </TabPanel>

              <TabPanel>
                <ModalBody style={{ height: '60vh', paddingLeft: 0, paddingRight: 0 }}>
                  <VestingSchedule />
                </ModalBody>
              </TabPanel>
            </Tabs>
          </form>
        </FormProvider>

        <ModalFooter>
          {grant.Id && (
            <Button
              variant="errorSecondary"
              onClick={() => setGrantToDelete({ grant, account } as any)}
              mr="auto"
            >
              Delete
            </Button>
          )}

          <Button variant="subtleInactive" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            type="submit"
            form="upsert_grant__form"
            isLoading={isSavingAccount}
            ml="8"
          >
            Save
          </Button>
        </ModalFooter>
      </Modal>

      {grantToDelete && (
        <DeleteStockGrantModal
          {...grantToDelete}
          clientId={clientId}
          onClose={() => setGrantToDelete(null)}
          onSuccess={onClose}
        />
      )}
    </>
  );
};
